import type { INavbarItem, INavbarProps } from './types';
import { defineComponent, computed, reactive, type CSSProperties, ref } from 'vue';
import Icon from '../icon';
import type { IBaseComponentProps } from '../base/types';
import Avatar from '../avatar';

import BellIcon from 'assets/img/svg/icons/bell.svg?url';
import { Spinner } from '../spinner';
import { type INotification } from 'findef';
import Dropdown from '../dropdown';
import type { DropdownItemGroup } from '../dropdown/types';
import type { IconFactor } from '@/types/elements/icon';
import Button from '../button';
import RefreshIcon from 'assets/img/svg/icons/refresh.svg?url';
import { GridColumn, GridRow } from '../grid';
import { getUserFullName } from '@/utils/user';
import { useSession } from '@/modules/auth/hooks/useSession';
import { useProviderImports } from '@/modules/asset/hooks/useProviderImports';
import { useGlobalProgress } from '../global-progress/hook';
import { FindexClient } from '@/integrations/findex';
import { useUserSettings } from '@/modules/settings/hooks/useUserSettings';
import Bubbletip from '../bubbletip';
import { pushImportAssetsModal } from '@/modules/asset/modals/import-assets';
import { pushAIConversationModal } from '@/modules/ai/modals/conversation';
import { insertAt, VEC2 } from 'math';
import WithBubbletip from '../with-bubbletip';
import { refreshInvestments } from '~/composables/useDataRefresh';
import { AutomaticBreadcrumbs } from 'components/automatic-breadcrumbs';
import { useNotifications } from '~/composables/useNotifications';
import { NotificationsWindow } from 'components/notifications-window';
import {
  EFloaterAnimationBehavior,
  EFloaterBehavior,
  EFloaterEvent,
  useFloater,
} from '~/composables/useFloater';
import { CircleBadge } from 'components/circle-badge';
import { useResponsive } from '~/composables/useResponsive';
interface INavItemProps extends IBaseComponentProps {
  icon?: IconFactor;
}

const NavItem = defineComponent<INavItemProps>(
  (props, { slots }) => {
    const icon = computed(() => props.icon);

    return () => (
      <div
        style={{
          cursor: 'pointer',
          userSelect: 'none',
        }}
        class="flex flex-row items-center gap-2 text-gray-700 text-sm font-medium relative"
      >
        {icon.value ? (
          <>
            <Icon src={icon.value} fill="gray-700" stroke="gray-700" />
            <div class="text-gray-700 text-sm font-medium">
              {slots.default ? slots.default() : ''}
            </div>
          </>
        ) : slots.default ? (
          slots.default()
        ) : (
          ''
        )}
      </div>
    );
  },
  { props: ['icon'] },
);

interface ISquareProps extends IBaseComponentProps {
  imageSrc?: string;
}

const Square = defineComponent(
  (props: ISquareProps, { slots }) => {
    return () => {
      return props.imageSrc ? (
        <img class="w-10 h-10 rounded-sm" src={props.imageSrc} alt="Default avatar" />
      ) : (
        <div class="w-10 h-10 p-1.5 rounded-sm border border-gray-300 justify-center items-center inline-flex">
          {slots.default ? slots.default() : ''}
        </div>
      );
    };
  },
  { props: ['imageSrc'] },
);

const NotificationsCircle = defineComponent<{ notifications: INotification[] }>({
  setup(props) {
    const count = computed(() => (props.notifications || []).length);
    const text = computed(() => (count.value > 99 ? '99+' : `${count.value}`));
    const elRef = ref<HTMLSpanElement | null>(null);

    watch(count, (next, prev) => {
      if (prev === undefined || next <= prev) return;
      const el = elRef.value;
      if (!el) return;
      el.classList.remove('notif-badge-pop');
      void el.offsetWidth;
      el.classList.add('notif-badge-pop');
    });

    const style = computed((): CSSProperties => {
      return {
        position: 'absolute',
        top: '-6px',
        fontSize: '10px',
        right: '-18px',
        width: '1rem',
        height: '1rem',
        fontWeight: '500'
      };
    });

    return () => {
      return (
        <span
          ref={elRef}
          class="flex me-3 shadow-md bg-red-500 rounded-full text-white content-center justify-center items-center"
          style={style.value}
        >
          {text.value}
        </span>
      );
    };
  },
});

NotificationsCircle.props = ['notifications'];

type NavbarState = {
  notificationsExpanded: boolean;
  userMenuExpanded: boolean;
};

const Navbar = defineComponent<INavbarProps>({
  setup(_props) {
    const route = useRoute();
    const router = useRouter();
    const session = useSession();
    const settings = useUserSettings();
    useResponsive();

    const currentUser = computed(() => session.state.user);

    const isAIAgentEnabled = computed(() => {
      return true;
      //const user = currentUser.value;
      //if (!user) return false;
      //const caps = user.capabilities;
      //if (!caps || caps.length <= 0) return false;
      //return !!caps.find((it) => it === EUserCapability.AI_ASSISTANT);
    });

    const state = reactive<NavbarState>({
      notificationsExpanded: false,
      userMenuExpanded: false,
    });

    const notificationRef = ref();
    const notificationsHandle = useNotifications();
    const notifications = computed(() => notificationsHandle.notifications.value);
    const notificationsFloater = useFloater({
      snapRef: notificationRef,
      behavior: EFloaterBehavior.SNAP,
      animationBehavior: EFloaterAnimationBehavior.SLIDE_FROM_BASE,
      animationDuration: 0.25,
      placementDirection: VEC2(-0.5, 1),
      deactivateOnClickOutside: true,
      ignoreClickOutsideRefs: [notificationRef],
    });

    const handleClickNotificationsButton = async () => {
      notificationsFloater.setActive((active) => !active);
    };

    notificationsFloater.events.subscribe(EFloaterEvent.BEFORE_INACTIVE, () => {
      if (notifications.value.length > 0) {
        notificationsHandle.markAllRead().catch((e) => console.error(e));
      }
    });

    // const notificationsQuery = useMemoAsync<ISavedDocument<INotification>[]>(async () => {
    //   const [items] = await FindexClient.getNotifications(ENotificationStatus.UNREAD)
    //   return items as ISavedDocument<INotification>[]
    // })

    // const notifications = computed(() =>
    //   notificationsQuery.loading ? [] : notificationsQuery.data || []
    // )

    const providerImportsHandler = useProviderImports();
    const globalProgress = useGlobalProgress();

    /* const providersTooltip = computed(() => {
      const numProviderImports = providerImportsHandler.providerImports.value.length;
      const numExpired = providerImportsHandler.expiredCount.value;

      return (
        <div>
          <div>
            <span>Connected providers:</span>{' '}
            <span class="font-semibold text-green-500">{numProviderImports}</span>
          </div>
          <div>
            <span>Expired providers:</span>{' '}
            <span class="font-semibold text-red-500">{numExpired}</span>
          </div>
        </div>
      );
      }); */

    //const refreshVariant = computed(() => {
    //  if (providerImportsHandler.expiredCount.value > 0) return 'error100'
    //  return 'findex100'
    //})

    const setUserMenuExpanded = (expanded: boolean) => {
      state.userMenuExpanded = expanded;
    };

    const handleClickAI = () => {
      pushAIConversationModal();
    };

    const handleClickUser = () => {
      setUserMenuExpanded(!state.userMenuExpanded);
    };

    const handleClickRefreshProvidersButton = () => {
      pushImportAssetsModal({
        breadcrumbs: false,
        title: 'Reconnect Providers ',
        onSave: () => {
          refreshInvestments();
        },
      });
    };

    const signOut = () => {
      session.logout().catch((e) => console.error(e));
    };

    const goToSettings = () => {
      router.push('/settings/profile');
    };

    const userMenuGroups: DropdownItemGroup[] = [
      {
        items: [
          {
            label: 'Settings',
            onClick: () => goToSettings(),
          },
        ],
      },
      {
        items: [
          {
            label: 'Sign out',
            onClick: () => signOut(),
          },
        ],
      },
    ];

    const currencyRef = ref();

    const menuRef = ref();

    const tooltipBody = computed(() => {
      // const numProviderImports = providerImportsHandler.providerImports.value.length;
      const numConnected = providerImportsHandler.connectedCount.value;
      const numExpired = providerImportsHandler.expiredCount.value;
      return (
        <div class="min-w-[185px] min-h-[100px]  shadow-xl rounded-md">
          <div class="h-[42px] px-3 py-4 bg-gray-100 rounded-xs justify-start items-center">
            <div class="text-gray-500 text-sm font-['Inter'] leading-[14px] flex justify-between">
              <span class="text-gray-500 font-medium">Providers</span>
              <span class="font-medium text-gray-500">
                {providerImportsHandler.totalCount.value}
              </span>
            </div>
          </div>
          <div class="mt-1 px-3 py-2">
            <div class="flex justify-between">
              <span class="text-gray-600 font-medium">Connected</span>
              <span class="font-medium text-primary-500">{numConnected}</span>
            </div>

            <div class="mt-3 flex justify-between">
              <span class="text-gray-600 font-medium">Session Expired</span>
              <span class="font-medium text-red-500">{numExpired}</span>
            </div>
          </div>
        </div>
      );
    });

    const rightItems = computed((): INavbarItem[] => {
      let items: INavbarItem[] = [
        ...(isAIAgentEnabled.value && !route.path.includes('investor-relations')
          ? [
            {
              body: () => {
                return (
                  <NavItem>
                    <Button
                      icon={{
                        src: async () => import('assets/img/svg/icons/ai-avatar.svg?raw'),
                        fill: 'currentColor',
                        stroke: 'none',
                        size: '1.25rem',
                      }}
                      variant="primary"
                      onClick={handleClickAI}
                    >
                      <span class="font-semibold">Midas AI</span>
                    </Button>
                  </NavItem>
                );
              },
            },
          ]
          : []),
        {
          body: () => {
            return (
              <NavItem>
                <div class="flex items-center justify-start h-10 text-gray-400 border border-gray-300 rounded-sm px-4 py-2 text-sm cursor-default whitespace-nowrap">
                  {'Early Access'}
                </div>
              </NavItem>
            );
          },
        },
        ...(!route.path.includes('investor-relations')
          ? [
            {
              body: () => (
                <GridColumn startRight>
                  <WithBubbletip
                    tooltip={{
                      variant: 'white',
                      placement: 'bottom',
                      noPadding: true,
                      body: () => {
                        return tooltipBody.value;
                      },
                    }}
                  >
                    <NavItem>
                      <div class="relative inline-flex">
                        {providerImportsHandler.expiredCount.value > 0 && (
                          <CircleBadge
                            variant="red"
                            style={{
                              position: "absolute",
                              top: "-6px",
                              right: "-18px",
                              width: "1rem",
                              height: "1rem",
                              fontSize: "10px",
                            }}
                            value={providerImportsHandler.expiredCount.value}
                          />
                        )}
                        <button
                          onClick={handleClickRefreshProvidersButton}
                          class={[
                            'w-10 h-10 rounded-sm border justify-center items-center inline-flex text-xs',
                            providerImportsHandler.expiredCount.value > 0
                              ? 'text-red-500 border-gray-300 hover:bg-red-100 hover:border-red-500'
                              : 'text-primary-500 border-gray-300 hover:bg-primary-100 hover:border-primary-500',
                          ].join(' ')}
                        >
                          {providerImportsHandler.loading.value || globalProgress.running.value ? (
                            <Spinner />
                          ) : (
                            <Icon src={RefreshIcon} size="14" stroke="currentColor" />
                          )}
                        </button>
                      </div>

                    </NavItem>
                  </WithBubbletip>
                </GridColumn>
              ),
            },
          ]
          : []),
        {
          body: () => (
            <NavItem ref={notificationRef} onClick={handleClickNotificationsButton}>
              <Square class="group text-gray-500 bg-transparent hover:border-primary-500 hover:bg-primary-100 hover:text-primary-500">
                {notificationsHandle.isLoading.value ? (
                  <Spinner />
                ) : (
                  <Icon
                    class="text-gray-500 group-hover:text-primary-500"
                    src={BellIcon}
                    fill="currentColor"
                  />
                )}
                {notifications.value.length > 0 && (
                  <NotificationsCircle notifications={notifications.value} />
                )}
              </Square>
              <NotificationsWindow
                style={notificationsFloater.elementProps.value.style}
                ref={notificationsFloater.elementRef}
                hook={notificationsHandle}
                isActive={notificationsFloater.isActive.value}
              />
            </NavItem>
          ),
        },
        {
          body: () => (
            <NavItem ref={currencyRef}>
              <Square class="text-xs text-gray-400 bg-transparent hover:border-primary-500 hover:bg-primary-100 hover:text-primary-500 cursor-default">
                {settings.loading.value ? <Spinner /> : settings.currency.value}
              </Square>
            </NavItem>
          ),
        },
        ...(currentUser.value
          ? [
            {
              body: () => {
                const user = currentUser.value;
                const userFullName = user ? getUserFullName(user) : '';
                return user ? (
                  <NavItem ref={menuRef} onClick={handleClickUser} data-testid="user-menu-trigger">
                    <Avatar
                      colorPreference={user.color}
                      fullname={userFullName}
                      src={user.avatar ? FindexClient.urlForAttachment(user.avatar) : undefined}
                      class="hover:opacity-80"
                    />
                    <Dropdown
                      triggerRef={menuRef}
                      setExpanded={setUserMenuExpanded}
                      expanded={state.userMenuExpanded}
                      groups={userMenuGroups}
                      header={{
                        title: userFullName || '',
                        subtitle: user.email === userFullName ? undefined : user.email,
                      }}
                    />
                  </NavItem>
                ) : (
                  <div />
                );
              },
            },
          ]
          : []),
      ];

      items = insertAt(items, 1, {
        body: () => {
          return (
            <div class="grid items-center justify-center">
              <div class="h-10 w-px bg-gray-300" />
            </div>
          );
        },
      });

      return items;
    });

    return () => {
      return (
        <div class="w-full z-105 max-w-[1600px] pl-4 pr-6 h-[62px]">
          <GridRow
            style={{
              position: 'relative',
              zIndex: '104',
            }}
            layout="auto 1fr"
          >
            <GridColumn>
              <AutomaticBreadcrumbs />
            </GridColumn>

            <div class="grid grid-cols-[repeat(auto-fit,minmax(1rem,max-content))] gap-2 justify-end items-center">
              {rightItems.value.map((it, i) => {
                return <it.body key={`nav-right-${i}`} />;
              })}
            </div>
          </GridRow>

          <Bubbletip
            placement="bottom"
            variant="white"
            triggerRef={currencyRef}
            body={() => (
              <div
                style={{
                  width: '280px',
                  padding: '3px',
                  whiteSpace: 'normal',
                }}
              >
                <p style={{ margin: 0 }}>
                  This is the selected currency that all of your investments will be converted to
                  and displayed as.
                </p>
                <p style={{ margin: '8px 0 0' }}>Can be changed in settings.</p>
              </div>
            )}
          />
        </div>
      );
    };
  },
});

Navbar.props = [];

export default Navbar;
